// POS → Restaurant → Áreas y Mesas (/pos/rest-mesas). MODO DISEÑO del plano:
// áreas del local y mesas dibujadas en la grilla. Operable por teclado:
// selecciona una mesa y muévela con las FLECHAS (Shift+flechas = tamaño);
// los cambios de posición se guardan al soltar (botón o Enter).
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import { Image as ImageIcon, LayoutGrid, Plus, QrCode, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { SelectorLocal, Campo, Modal, fieldCls, posRpc } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  GRID_COLS, GRID_ROWS, MESA_CLS, MesaT, RestEstadoT, elementoVisible,
  estadoMesa, restEstadoQuery, useLocalRestaurant,
} from './restaurante';

interface MesaEdit {
  id: string | null; area_id: string; numero: string; capacidad: string;
  forma: MesaT['forma']; pos_x: number; pos_y: number; ancho: number; alto: number;
}

export function MesasRestaurant() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const rootRef = useRef<HTMLDivElement>(null);
  const { locationId, setLocationId, locations } = useLocalRestaurant();
  const { data: estado } = useQuery<RestEstadoT>(restEstadoQuery(locationId));

  const [areaId, setAreaId] = useState('');
  const [nuevaArea, setNuevaArea] = useState('');
  const [edit, setEdit] = useState<MesaEdit | null>(null);
  const [sucio, setSucio] = useState(false);
  const [qr, setQr] = useState<{ numero: string; url: string } | null>(null);

  const areas = useMemo(() => estado?.areas ?? [], [estado]);
  const area = areas.find((a) => a.id === areaId) ?? areas[0];
  useEffect(() => { if (!areaId && areas.length) setAreaId(areas[0].id); }, [areas, areaId]);
  const mesas = useMemo(() => (estado?.mesas ?? []).filter((m) => m.area_id === area?.id), [estado, area]);

  const invalidar = () => void qc.invalidateQueries({ queryKey: ['rest-estado'] });
  const rpc = async (fn: string, body: Record<string, unknown>, ok?: string) => {
    try { const r = await posRpc(fn, body); invalidar(); if (ok) toast.success(ok); return r; }
    catch (e) { toast.error(getErrorMessage(e)); return null; }
  };

  const guardarMesa = async () => {
    if (!edit) return;
    const r = await rpc('rest_mesa_guardar', {
      _data: { area_id: edit.area_id, numero: Number(edit.numero), capacidad: Number(edit.capacidad) || 4,
        forma: edit.forma, pos_x: edit.pos_x, pos_y: edit.pos_y, ancho: edit.ancho, alto: edit.alto },
      _id: edit.id,
    }, edit.id ? 'Mesa actualizada' : 'Mesa creada');
    if (r) { setSucio(false); if (!edit.id) setEdit(null); }
  };

  // mover/redimensionar por teclado
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (!elementoVisible(rootRef.current) || !edit) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      const d = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[e.key];
      if (d) {
        e.preventDefault();
        setSucio(true);
        setEdit((m) => {
          if (!m) return m;
          if (e.shiftKey) {
            return { ...m, ancho: Math.max(1, Math.min(GRID_COLS, m.ancho + d[0])), alto: Math.max(1, Math.min(GRID_ROWS, m.alto + d[1])) };
          }
          return { ...m,
            pos_x: Math.max(1, Math.min(GRID_COLS - m.ancho + 1, m.pos_x + d[0])),
            pos_y: Math.max(1, Math.min(GRID_ROWS - m.alto + 1, m.pos_y + d[1])) };
        });
        return;
      }
      if (e.key === 'Enter' && sucio) { e.preventDefault(); void guardarMesa(); }
      if (e.key === 'Escape') { e.preventDefault(); setEdit(null); setSucio(false); }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, sucio]);

  const seleccionar = (m: MesaT) => setEdit({
    id: m.id, area_id: m.area_id, numero: String(m.numero), capacidad: String(m.capacidad),
    forma: m.forma, pos_x: m.pos_x, pos_y: m.pos_y, ancho: m.ancho, alto: m.alto,
  });

  return (
    <div ref={rootRef} className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-xl font-semibold flex items-center gap-2"><LayoutGrid className="w-5 h-5" /> Áreas y Mesas</h1>
        <SelectorLocal locationId={locationId} setLocationId={setLocationId} locations={locations} />
        <span className="text-xs text-muted-foreground ml-auto">
          Selecciona una mesa: flechas = mover · Shift+flechas = tamaño · Enter = guardar
        </span>
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Áreas del local</CardTitle></CardHeader>
        <CardContent className="flex items-center gap-2 flex-wrap">
          {areas.map((a) => (
            <span key={a.id} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-sm cursor-pointer
                ${a.id === area?.id ? 'border-primary text-primary font-medium' : ''}`}
                  onClick={() => setAreaId(a.id)}>
              {a.nombre}
              <button title="Eliminar área" onClick={async (e) => {
                e.stopPropagation();
                if (await dlg.confirmar(`¿Eliminar el área "${a.nombre}"?`, { detalle: 'Debe estar sin mesas.', peligro: true }))
                  void rpc('rest_area_guardar', { _data: {}, _id: a.id, _eliminar: true }, 'Área eliminada');
              }}><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
            </span>
          ))}
          <input className={`${fieldCls} !w-48`} placeholder="Nueva área (Salón, Terraza…)" value={nuevaArea}
                 onChange={(e) => setNuevaArea(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && nuevaArea.trim()) {
                     void rpc('rest_area_guardar', { _data: { location_id: locationId, nombre: nuevaArea.trim() } }, 'Área creada');
                     setNuevaArea('');
                   }
                 }} />
          {area && (
            <div className="w-full border-t pt-2 mt-1 flex items-center gap-3 flex-wrap">
              <span className="text-sm">Fondo de <span className="font-medium">{area.nombre}</span>:</span>
              {area.imagen_fondo
                ? <img src={area.imagen_fondo} alt="fondo" className="h-10 w-16 object-cover rounded border" />
                : <span className="text-xs text-muted-foreground">sin imagen</span>}
              <label className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border text-sm cursor-pointer">
                <ImageIcon className="w-4 h-4" /> {area.imagen_fondo ? 'Cambiar' : 'Subir'} imagen
                <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const r = new FileReader();
                  r.onload = () => void rpc('rest_area_guardar',
                    { _data: { location_id: locationId, imagen_fondo: String(r.result) }, _id: area.id }, 'Fondo actualizado');
                  r.readAsDataURL(file);
                  e.target.value = '';
                }} />
              </label>
              {area.imagen_fondo && (
                <button className="text-xs text-red-500 hover:underline" onClick={() =>
                  void rpc('rest_area_guardar', { _data: { location_id: locationId, imagen_fondo: '' }, _id: area.id }, 'Fondo quitado')}>
                  Quitar
                </button>
              )}
              <span className="text-xs text-muted-foreground">Se ve detrás del plano en el Salón.</span>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 border rounded-lg bg-muted/20 overflow-auto bg-center bg-cover"
             style={area?.imagen_fondo ? { backgroundImage: `url(${area.imagen_fondo})` } : undefined}>
          <div className="grid gap-1 p-3"
               style={{ gridTemplateColumns: `repeat(${GRID_COLS}, minmax(24px, 1fr))`,
                        gridTemplateRows: `repeat(${GRID_ROWS}, minmax(30px, 1fr))`, minHeight: '55vh' }}>
            {mesas.map((m) => {
              const enEdicion = edit?.id === m.id;
              const vista = enEdicion ? edit : m;
              return (
                <button key={m.id}
                  className={`border-2 flex items-center justify-center font-bold text-lg
                    ${MESA_CLS[estadoMesa(m)]} ${(enEdicion ? edit.forma : m.forma) === 'circulo' ? 'rounded-full' : 'rounded-lg'}
                    ${enEdicion ? 'ring-4 ring-primary' : ''}`}
                  style={{ gridColumn: `${vista.pos_x} / span ${vista.ancho}`, gridRow: `${vista.pos_y} / span ${vista.alto}` }}
                  onClick={() => seleccionar(m)}>
                  {m.numero}
                </button>
              );
            })}
          </div>
        </div>

        <Card className="h-fit">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{edit?.id ? `Mesa ${edit.numero}` : edit ? 'Nueva mesa' : 'Mesa'}</CardTitle>
              <Button size="sm" variant="outline" disabled={!area}
                      onClick={() => setEdit({ id: null, area_id: area!.id,
                        // sugerencia: siguiente número libre DE LA SALA (número único por sala, 697)
                        numero: String(mesas.reduce((mx, m) => Math.max(mx, m.numero), 0) + 1), capacidad: '4',
                        forma: 'cuadrado', pos_x: 1, pos_y: 1, ancho: 2, alto: 2 })}>
                <Plus className="w-4 h-4 mr-1" /> Nueva
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {!edit && <p className="text-sm text-muted-foreground">Haz clic en una mesa del plano para editarla, o crea una nueva.</p>}
            {edit && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Campo label="Número *">
                    <input className={fieldCls} type="number" min="1" value={edit.numero}
                           onChange={(e) => { setEdit({ ...edit, numero: e.target.value }); setSucio(true); }} />
                  </Campo>
                  <Campo label="Capacidad">
                    <input className={fieldCls} type="number" min="1" value={edit.capacidad}
                           onChange={(e) => { setEdit({ ...edit, capacidad: e.target.value }); setSucio(true); }} />
                  </Campo>
                  <Campo label="Forma">
                    <select className={fieldCls} value={edit.forma}
                            onChange={(e) => { setEdit({ ...edit, forma: e.target.value as MesaT['forma'] }); setSucio(true); }}>
                      <option value="cuadrado">Cuadrada</option>
                      <option value="circulo">Redonda</option>
                      <option value="rectangulo">Rectangular</option>
                      <option value="barra">Barra</option>
                    </select>
                  </Campo>
                  <Campo label="Área">
                    <select className={fieldCls} value={edit.area_id}
                            onChange={(e) => { setEdit({ ...edit, area_id: e.target.value }); setSucio(true); }}>
                      {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                    </select>
                  </Campo>
                </div>
                <div className="text-xs text-muted-foreground">
                  Posición ({edit.pos_x}, {edit.pos_y}) · tamaño {edit.ancho}×{edit.alto} — muévela con las flechas.
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => void guardarMesa()} disabled={!edit.numero}>
                    <Save className="w-4 h-4 mr-1" /> Guardar
                  </Button>
                  {edit.id && (
                    <Button size="sm" variant="outline" title="QR de la carta para esta mesa" onClick={async () => {
                      try {
                        const r = await posRpc<{ token: string }>('rest_mesa_qr', { _mesa: edit.id });
                        setQr({ numero: edit.numero, url: `${window.location.origin}/carta/${r.token}` });
                      } catch (e) { toast.error(getErrorMessage(e)); }
                    }}><QrCode className="w-4 h-4" /></Button>
                  )}
                  {edit.id && (
                    <Button size="sm" variant="ghost" onClick={async () => {
                      if (await dlg.confirmar('¿Eliminar la mesa?', { peligro: true }))
                        void rpc('rest_mesa_guardar', { _data: {}, _id: edit.id, _eliminar: true }, 'Mesa eliminada')
                          .then(() => setEdit(null));
                    }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  )}
                  <Button size="sm" variant="outline" className="ml-auto" onClick={() => { setEdit(null); setSucio(false); }}>Cerrar</Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {qr && (
        <Modal title={`QR de la carta — Mesa ${qr.numero}`} onClose={() => setQr(null)}>
          <div className="space-y-3 text-center">
            <div className="inline-block bg-white p-4 rounded-lg">
              <QRCodeSVG value={qr.url} size={220} />
            </div>
            <div className="text-xs font-mono break-all text-muted-foreground">{qr.url}</div>
            <p className="text-xs text-muted-foreground">
              Imprímelo y pégalo en la mesa: el cliente ve la carta y pide desde su teléfono
              (las líneas llegan al mesero como pendientes). El token es permanente para esta mesa.
            </p>
            <Button size="sm" variant="outline" onClick={() => window.print()}>Imprimir</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}
